import { useEffect, useState } from 'react';
import { apiFetch, downloadLibraryArchive } from '../utils/api';

const states = { scheduled: 'Na fila', collecting: 'Coletando anúncios', downloading: 'Baixando mídias', done: 'Concluído', partial: 'Resultado parcial', error: 'Falhou' };
const running = j => j && ['scheduled', 'collecting', 'downloading'].includes(j.status);
const date = value => value ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Não informado';
const size = value => value ? `${(value / 1024 / 1024).toFixed(1)} MB` : '';
const publicUrl = path => path?.startsWith('/') ? `${import.meta.env.VITE_API_URL || ''}${path}` : path;

function activeTime(ad) {
  if (ad.total_active_seconds != null) {
    const hours = ad.total_active_seconds / 3600;
    return `${hours >= 24 ? `${Math.floor(hours / 24)}d ` : ''}${Math.floor(hours % 24)}h ${Math.floor(ad.total_active_seconds / 60) % 60}min (Meta)`;
  }
  const end = ad.is_active ? ad.observed_at : ad.ended_at;
  if (!ad.started_at || !end) return 'Não informado';
  const days = Math.max(0, Math.floor((new Date(end) - new Date(ad.started_at)) / 86400000));
  return `${days} dias desde o início${ad.is_active ? ' até a coleta' : ' até o fim'}`;
}

function CopyField({ label, text }) {
  return <div><div style={s.label}>{label}</div><div style={s.copy}>{text || 'Não informado pela Meta'}</div></div>;
}

function Group({ group, onPreview }) {
  const [expanded, setExpanded] = useState(false);
  const ad = group.ads[0];
  const first = ad.assets[0];
  const [copyState, setCopyState] = useState('');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Texto principal:\n${ad.primary_text || ''}\n\nTítulo:\n${ad.title || ''}\n\nDescrição:\n${ad.description || ''}`);
      setCopyState('Copiado');
    } catch { setCopyState('Não foi possível copiar. Selecione o texto abaixo.'); }
  };
  return <article style={s.group}>
    <button style={s.groupHeader} onClick={() => setExpanded(v => !v)} aria-expanded={expanded}>
      {(first?.preview_path || first?.preview_url) && <img src={publicUrl(first.preview_path || (first.kind === 'image' && first.path ? first.path : first.preview_url))} alt="" loading="lazy" style={s.thumb} onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={s.groupTitle}>{ad.title || ad.primary_text || ad.page_name || 'Anúncio sem título'}</div>
        <div style={s.dim}>{ad.page_name} · {ad.format || 'Formato não informado'}</div>
        <div style={s.dim}>Publicações: {date(group.first_started_at)}{group.last_started_at !== group.first_started_at && ` a ${date(group.last_started_at)}`}</div>
      </div>
      <div style={s.count}><strong>{group.count}</strong><span>ID{group.count > 1 ? 's' : ''} identificado{group.count > 1 ? 's' : ''}</span><small>{group.active_count} ativo{group.active_count !== 1 ? 's' : ''} na coleta</small>{group.meta_reported_count != null && <small style={s.dim}>Meta: {group.meta_reported_count} anúncios</small>}</div>
      <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
    </button>
    {expanded && <div style={s.groupBody}>
      {!group.verified && <p style={s.warning}>Mídia não verificada. Este anúncio permanece separado para evitar agrupamento incorreto.</p>}
      {group.meta_reported_count != null && <p style={s.dim}>A Meta informa {group.meta_reported_count} anúncios nos agrupamentos de origem. Aqui, {group.count} IDs foram identificados com esta mídia e estes textos.</p>}
      <div style={s.copyGrid}>
        <CopyField label="Texto principal" text={ad.primary_text} />
        <div style={s.stack}><CopyField label="Título" text={ad.title} /><CopyField label="Descrição" text={ad.description} /></div>
      </div>
      <button style={s.button} onClick={copy}>Copiar textos</button> {copyState && <span role="status" style={s.dim}>{copyState}</span>}
      {ad.cards?.map((card, i) => <div key={i} style={s.cardCopy}><div style={s.label}>Card {i + 1}</div><CopyField label="Texto" text={card.primary_text} /><CopyField label="Título" text={card.title} /><CopyField label="Descrição" text={card.description} /></div>)}
      <div style={s.assets}>
        {ad.media_error && <p style={s.warning}>{ad.media_error}</p>}
        {ad.assets.map((asset, i) => <div key={i} style={s.asset}>
          <span>{asset.kind === 'video' ? 'Vídeo' : 'Imagem'} {ad.assets.length > 1 ? i + 1 : ''} · {asset.quality || 'Pendente'} {size(asset.size_bytes)}</span>
          {asset.path && <button style={s.button} onClick={() => onPreview(asset)}>Visualizar</button>}
          {asset.path && <a style={s.link} href={publicUrl(asset.path)} download={asset.path.split('/').pop()} target="_blank" rel="noopener noreferrer">Baixar arquivo</a>}
          {asset.quality_note && <span style={s.warning}>{asset.quality_note}</span>}
          {asset.error && <span style={s.warning}>{asset.error}</span>}
        </div>)}
      </div>
      <div style={s.tableWrap}><table style={s.table}>
        <thead><tr>{['ID do anúncio', 'Publicado em', 'Status na coleta', 'Tempo ativo / período', 'Encerrado em'].map(t => <th style={s.th} key={t}>{t}</th>)}</tr></thead>
        <tbody>{group.ads.map(item => <tr key={item.archive_id}>
          <td style={s.td}><a href={item.preview_url} target="_blank" rel="noopener noreferrer" style={s.link}>{item.archive_id}</a></td>
          <td style={s.td}>{date(item.started_at)}</td>
          <td style={s.td}>{item.is_active === true ? 'Ativo' : item.is_active === false ? 'Inativo' : 'Não informado'}</td>
          <td style={s.td}>{activeTime(item)}</td><td style={s.td}>{item.is_active ? '—' : date(item.ended_at)}</td>
        </tr>)}</tbody>
      </table></div>
    </div>}
  </article>;
}

export default function AdLibraryDownloader() {
  const [url, setUrl] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('count');
  const [preview, setPreview] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer;
    async function load() {
      try {
        const data = await apiFetch('/api/ad-library-import');
        if (!alive) return;
        setJobs(data.jobs);
        setSelected(current => current || data.jobs[0]?.job_id || '');
        if (data.jobs.some(running)) timer = setTimeout(load, 4000);
      } catch (err) { if (alive) setError(err.message); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh]);

  useEffect(() => {
    if (!selected) return;
    let alive = true;
    let timer;
    setDetail(null);
    async function load() {
      try {
        const data = await apiFetch(`/api/ad-library-import/${selected}`);
        if (!alive) return;
        setDetail(data);
        if (running(data)) timer = setTimeout(load, 4000);
      } catch (err) { if (alive) setError(err.message); }
    }
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [selected, refresh]);

  useEffect(() => {
    if (!preview) return;
    const onKey = e => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const submit = async e => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const job = await apiFetch('/api/ad-library-import', { method: 'POST', body: JSON.stringify({ url: url.trim() }) });
      setSelected(job.job_id); setRefresh(v => v + 1); setUrl('');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };
  const download = async () => {
    setDownloading(true); setError('');
    try { await downloadLibraryArchive(selected); }
    catch (err) { setError(err.message); }
    finally { setDownloading(false); }
  };
  const groups = (detail?.groups || []).filter(g => !search || g.ads.some(a => [a.primary_text, a.title, a.description, a.page_name, a.archive_id].some(t => t?.toLowerCase().includes(search.toLowerCase()))));
  groups.sort((a, b) => sort === 'recent' ? (b.last_started_at || '').localeCompare(a.last_started_at || '') : sort === 'active' ? b.active_count - a.active_count : b.count - a.count);
  const summary = detail?.summary || {};

  return <div style={s.page}>
    <h1 style={s.title}>Baixar anúncios</h1>
    <p style={s.subtitle}>Cole o link da Biblioteca de Anúncios da Meta. Salve vídeos e imagens na melhor qualidade disponibilizada, com os textos e as datas de cada publicação.</p>
    <form onSubmit={submit} style={s.form}>
      <input style={s.input} type="url" value={url} onChange={e => setUrl(e.target.value)} required aria-label="Link da Biblioteca de Anúncios" placeholder="https://www.facebook.com/ads/library/?view_all_page_id=..." />
      <button style={s.primary} disabled={submitting} type="submit">{submitting ? 'Iniciando…' : 'Importar e baixar'}</button>
    </form>
    <p style={s.dim}>Os filtros do link são preservados. Até 1.000 anúncios por importação; coletas incompletas são sinalizadas.</p>
    {error && <div role="alert" style={s.error}>{error} <button style={s.button} onClick={() => { setError(''); setRefresh(v => v + 1); }}>Atualizar</button></div>}
    <div style={s.toolbar}>
      <label style={{ ...s.label, minWidth: 0, maxWidth: '100%' }}>Histórico <select style={s.select} value={selected} onChange={e => { setSelected(e.target.value); setSearch(''); }} aria-label="Importação">
        {!jobs.length && <option value="">{loading ? 'Carregando…' : 'Nenhuma importação'}</option>}
        {jobs.map(j => <option key={j.job_id} value={j.job_id}>{date(j.created_at)} {new Date(j.created_at).toLocaleTimeString('pt-BR')} · {states[j.status]} · {j.summary.collected ?? j.progress.collected ?? 0} anúncios</option>)}
      </select></label>
      <button style={s.button} onClick={() => setRefresh(v => v + 1)}>Atualizar</button>
    </div>
    {loading && <div style={s.skeleton} aria-label="Carregando importações" />}
    {!loading && !jobs.length && <div style={s.empty}>Cole um link acima para criar sua primeira coleção de anúncios.</div>}
    {selected && !detail && <div style={s.skeleton} aria-label="Carregando anúncios" />}
    {detail && <>
      <div style={s.jobInfo}>
        <strong>{states[detail.status]}</strong>
        <a href={detail.url} target="_blank" rel="noopener noreferrer" style={{ ...s.link, overflowWrap: 'anywhere', minWidth: 0 }}>Abrir busca original na Meta</a>
        {running(detail) && <span role="status" style={s.dim}>{detail.progress.collected || 0} identificados · {detail.progress.processed || 0} processados</span>}
        {['done', 'partial'].includes(detail.status) && <button style={s.primary} onClick={download} disabled={downloading}>{downloading ? 'Preparando download…' : 'Baixar pacote ZIP'}</button>}
        {!running(detail) && <button style={s.button} onClick={() => setUrl(detail.url)}>Importar novamente</button>}
      </div>
      {detail.error && <p role="alert" style={s.error}>{detail.error}</p>}
      {(summary.warnings || []).map((w, i) => <p style={s.warning} key={i}>{w}</p>)}
      {summary.collected != null && <>
        <div style={s.stats}>
          {[[summary.collected, 'Anúncios identificados'], [summary.group_count, 'Grupos de anúncios'], [summary.reported_total ?? '—', 'Resultados informados pela Meta'], [size(summary.size_bytes) || '0 MB', 'Mídias salvas']].map(([value, label]) => <div style={s.stat} key={label}><strong>{value}</strong><span style={s.dim}>{label}</span></div>)}
        </div>
        <p style={s.dim}>Grupos da mesma página, com mídia idêntica e os mesmos textos, independentemente da data. Status e tempo referem-se à coleta em {date(summary.observed_at)}. O período desde a publicação pode incluir pausas.</p>
        <div style={s.toolbar}>
          <input style={s.input} value={search} onChange={e => setSearch(e.target.value)} aria-label="Filtrar anúncios" placeholder="Buscar por texto, título ou ID…" />
          <select style={s.select} value={sort} onChange={e => setSort(e.target.value)} aria-label="Ordenação"><option value="count">Mais anúncios iguais</option><option value="active">Mais anúncios ativos</option><option value="recent">Publicação mais recente</option></select>
        </div>
        {groups.map(g => <Group key={`${selected}:${g.key}`} group={g} onPreview={setPreview} />)}
        {!groups.length && <div style={s.empty}>{search ? 'Nenhum grupo corresponde à busca.' : 'Nenhum anúncio encontrado com os filtros deste link.'}</div>}
      </>}
    </>}
    {preview && <div style={s.overlay} onClick={() => setPreview(null)}>
      <div style={s.modal} role="dialog" aria-modal="true" aria-label="Prévia do anúncio" onClick={e => e.stopPropagation()}>
        <button style={s.button} autoFocus onClick={() => setPreview(null)}>Fechar</button>
        {preview.kind === 'video' ? <video src={publicUrl(preview.path)} controls autoPlay style={s.player} /> : <img src={publicUrl(preview.path)} alt="Criativo do anúncio" style={s.player} />}
      </div>
    </div>}
  </div>;
}

const s = {
  page: { padding: 24, maxWidth: 1400, margin: '0 auto', color: 'var(--text)' },
  title: { fontSize: 22, margin: '0 0 8px' }, subtitle: { color: 'var(--text2)', fontSize: 13, maxWidth: 800, lineHeight: 1.6 },
  form: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 },
  input: { flex: '1 1 260px', minWidth: 0, padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', font: 'inherit', fontSize: 13 },
  primary: { padding: '10px 16px', background: 'var(--accent)', color: 'var(--text)', border: '1px solid var(--accent)', borderRadius: 6, cursor: 'pointer', font: 'inherit', fontSize: 12 },
  button: { padding: '6px 10px', background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', font: 'inherit', fontSize: 12 },
  select: { padding: 9, maxWidth: '100%', boxSizing: 'border-box', background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, fontSize: 12 },
  toolbar: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '20px 0' },
  label: { fontSize: 11, color: 'var(--text3)', marginBottom: 5 }, dim: { fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 },
  error: { padding: 12, background: 'var(--surface2)', border: '1px solid var(--accent-r)', color: 'var(--accent-r)', borderRadius: 6, margin: '12px 0' },
  warning: { color: 'var(--accent-a)', fontSize: 12, lineHeight: 1.6 },
  link: { color: 'var(--accent)', textDecoration: 'none', fontSize: 12 },
  jobInfo: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, fontSize: 13, padding: '12px 0' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: '16px 0' },
  stat: { display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--surface)', padding: 16, border: '1px solid var(--border)', borderRadius: 8, fontSize: 22 },
  group: { marginBottom: 12, border: '1px solid var(--border)', background: 'var(--surface)', borderRadius: 8, overflow: 'hidden' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 16, textAlign: 'left', border: 0, background: 'transparent', color: 'var(--text)', cursor: 'pointer', font: 'inherit' },
  thumb: { width: 48, height: 68, objectFit: 'cover', borderRadius: 4, flexShrink: 0 },
  groupTitle: { fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 },
  count: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, fontSize: 12, flexShrink: 0 },
  groupBody: { padding: 18, borderTop: '1px solid var(--border)' },
  copyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 20, margin: '14px 0' },
  stack: { display: 'flex', flexDirection: 'column', gap: 16 }, copy: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13, lineHeight: 1.7 },
  cardCopy: { padding: 12, border: '1px solid var(--border)', marginTop: 12 }, assets: { margin: '16px 0' },
  asset: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 12 },
  tableWrap: { overflowX: 'auto' }, table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { color: 'var(--text3)', fontWeight: 500, textAlign: 'left', padding: 10, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: 10, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  empty: { textAlign: 'center', padding: 50, color: 'var(--text3)', fontSize: 13 },
  skeleton: { height: 110, background: 'var(--surface2)', borderRadius: 8, animation: 'pulse 1.5s infinite', marginTop: 14 },
  overlay: { position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 200, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modal: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, maxWidth: '95vw' },
  player: { maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8 },
};
